import {
  collection,
  query,
  where,
  getDocs,
  Timestamp,
  Firestore,
  CollectionReference,
  DocumentData
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { InventoryItem } from '../utils/types';
import { optimizedReportService } from './optimizedReportService';
import { simpleCache, createCacheKey } from '../utils/simpleCache';

// تعريف أنواع البيانات

// التحقق من توفر خدمة Firestore
const ensureFirestore = (): Firestore => {
  if (!db) {
    throw new Error('خدمة Firestore غير متوفرة');
  }
  return db;
};

// الحصول على مرجع مجموعة المبيعات
const getSalesCollection = (): CollectionReference<DocumentData> => {
  return collection(ensureFirestore(), 'sales');
};

// الحصول على مرجع مجموعة معاملات الدرج
const getDrawerTransactionsCollection = (): CollectionReference<DocumentData> => {
  return collection(ensureFirestore(), 'drawerTransactions');
};

const getMaintenanceCollection = (): CollectionReference<DocumentData> => {
  return collection(ensureFirestore(), 'maintenance');
};

const getExpensesCollection = (): CollectionReference<DocumentData> => {
  return collection(ensureFirestore(), 'expenses');
};

export interface MaintenanceStats {
  total: number;
  totalIncome: number;
  completedCount: number;
  pendingCount: number;
  inProgressCount: number;
  deliveredCount: number;
  cancelledCount: number;
  statusCounts: Record<string, number>;
  dailyCounts: Record<string, number>;
  totalRevenue: number;
  // إضافة معلومات قطع الغيار والربح
  totalPartsCost: number; // إجمالي تكلفة قطع الغيار (سعر الشراء)
  totalPartsRevenue: number; // إجمالي إيرادات قطع الغيار (سعر البيع)
  totalPartsProfit: number; // إجمالي ربح قطع الغيار
  totalMaintenanceFees: number; // إجمالي رسوم الصيانة (بدون قطع الغيار)
  netProfit: number; // صافي الربح (الإيرادات - تكلفة قطع الغيار)
  partsUsageStats: {
    totalPartsUsed: number; // إجمالي عدد قطع الغيار المستخدمة
    uniquePartsCount: number; // عدد أنواع قطع الغيار المختلفة
    mostUsedParts: Array<{
      name: string;
      quantity: number;
      cost: number;
      revenue: number;
      profit: number;
    }>;
  };
}

export interface InventoryStats {
  totalValue: number;
  totalItems: number;
  lowStockCount: number;
  lowStockItems: InventoryItem[];
  categoryDistribution: Record<string, number>;
}

export interface SalesStats {
  totalSales: number;
  totalTransactions: number;
  averageTransactionValue: number;
  topSellingItems: Array<{ name: string; quantity: number; total: number }>;
  dailySales: Record<string, number>;
}

/**
 * الحصول على إحصائيات طلبات الصيانة (محسن)
 */
export const getMaintenanceStats = async (
  startDate: Date,
  endDate: Date,
  branchId?: string
): Promise<MaintenanceStats> => {
  try {
    console.log(`📊 Using optimized maintenance stats service`);
    return await optimizedReportService.getOptimizedMaintenanceStats(startDate, endDate, branchId);
  } catch (error) {
    console.error('Error getting maintenance stats:', error);
    return {
      total: 0,
      totalIncome: 0,
      totalPartsCost: 0,
      totalPartsRevenue: 0,
      totalPartsProfit: 0,
      totalMaintenanceFees: 0,
      netProfit: 0,
      completedCount: 0,
      pendingCount: 0,
      inProgressCount: 0,
      deliveredCount: 0,
      cancelledCount: 0,
      statusCounts: {},
      dailyCounts: {},
      totalRevenue: 0,
      partsUsageStats: {
        totalPartsUsed: 0,
        uniquePartsCount: 0,
        mostUsedParts: []
      }
    };
  }
};

/**
 * الحصول على إحصائيات المخزون (محسن)
 */
export const getInventoryStats = async (): Promise<InventoryStats> => {
  try {
    console.log(`📦 Using optimized inventory stats service`);
    return await optimizedReportService.getOptimizedInventoryStats();
  } catch (error) {
    console.error('Error getting inventory stats:', error);
    return {
      totalValue: 0,
      totalItems: 0,
      lowStockCount: 0,
      lowStockItems: [],
      categoryDistribution: {}
    };
  }
};

/**
 * الحصول على المبيعات خلال فترة زمنية (محسن)
 */
export const getSalesStats = async (
  startDate: Date,
  endDate: Date,
  branchId?: string
): Promise<SalesStats> => {
  try {
    console.log(`💰 Using optimized sales stats service`);
    return await optimizedReportService.getOptimizedSalesStats(startDate, endDate, branchId);
  } catch (error) {
    console.error('Error getting sales stats:', error);
    return {
      totalSales: 0,
      totalTransactions: 0,
      averageTransactionValue: 0,
      topSellingItems: [],
      dailySales: {}
    };
  }
};

/**
 * الحصول على دخل الصيانة اليومي (محسن)
 */
export const getMaintenanceDailyIncome = async (
  startDate: Date,
  endDate: Date,
  branchId?: string
): Promise<Record<string, number>> => {
  try {
    console.log(`💰 Using optimized maintenance daily income service`);
    return await optimizedReportService.getOptimizedMaintenanceDailyIncome(startDate, endDate, branchId);
  } catch (error) {
    console.error('Error getting maintenance daily income:', error);
    return {};
  }
};

/**
 * حساب الدرج اليومي أو لفترة محددة (منطق مبسط وحديث)
 * الدرج = مجموع المدفوعات الفعلية من الصيانة + المبيعات + دفعات الديون - المصاريف
 */
export const getSimpleDrawerStats = async (
  startDate: Date,
  endDate: Date,
  branchId?: string
): Promise<{
  totalSales: number;
  totalMaintenance: number;
  totalDebtPayments: number;
  totalReturnsFromDrawer: number;
  totalExpenses: number;
  netTotal: number;
  dailyData: Record<string, {
    sales: number;
    maintenance: number;
    debtPayments: number;
    returns: number;
    expenses: number;
    net: number;
  }>;
}> => {
  // ✅ التحقق من التخزين المؤقت (محسّن)
  const cacheKey = createCacheKey(
    'treasury',
    startDate.toISOString().split('T')[0],
    endDate.toISOString().split('T')[0],
    branchId
  );

  const cached = simpleCache.get<any>(cacheKey);
  if (cached) {
    console.log('📦 استخدام بيانات الخزينة من التخزين المؤقت');
    return cached;
  }
  // سنحسب المبيعات من drawerTransactions فقط لتجنب الحساب المزدوج
  // المبيعات النقدية تُسجل في drawerTransactions كـ sale_payment
  // المبيعات الآجلة لا تؤثر على الخزينة مباشرة
  const salesByDay: Record<string, number> = {};
  let totalSales = 0;

  console.log('💰 سيتم حساب المبيعات من drawerTransactions لتجنب الحساب المزدوج...');

  // جلب بيانات المرتجعات وخصمها من المبيعات
  // البحث في كلا المجموعتين: returns (الجديدة) و sale_returns (القديمة)
  const salesReturnsByDay: Record<string, number> = {};
  let totalReturns = 0;

  console.log('🔄 جلب بيانات المرتجعات لخصمها من المبيعات...');

  // جلب المرتجعات من مجموعة returns (النظام الجديد)
  const newReturnsCollection = collection(db, 'returns');
  const newReturnsQuery = branchId
    ? query(newReturnsCollection, where('branchId', '==', branchId), where('createdAt', '>=', startDate), where('createdAt', '<=', endDate))
    : query(newReturnsCollection, where('createdAt', '>=', startDate), where('createdAt', '<=', endDate));

  try {
    const newReturnsSnapshot = await getDocs(newReturnsQuery);
    console.log(`🔄 تم العثور على ${newReturnsSnapshot.docs.length} مرتجع في مجموعة 'returns'`);

    newReturnsSnapshot.forEach(doc => {
      const data: any = doc.data();
      const createdAt = data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(data.createdAt);
      const dateStr = createdAt.toISOString().split('T')[0];
      const amount = data.totalAmount || 0;
      salesReturnsByDay[dateStr] = (salesReturnsByDay[dateStr] || 0) + amount;
      totalReturns += amount;
      console.log(`🔄 مرتجع جديد: ${amount} في ${dateStr} (مرتجع: ${doc.id.substring(0, 8)})`);
    });
  } catch (error) {
    console.warn('⚠️ خطأ في جلب المرتجعات الجديدة:', error);
  }

  // جلب المرتجعات من مجموعة sale_returns (النظام القديم)
  const oldReturnsCollection = collection(db, 'sale_returns');
  const oldReturnsQuery = branchId
    ? query(oldReturnsCollection, where('branchId', '==', branchId), where('createdAt', '>=', startDate), where('createdAt', '<=', endDate))
    : query(oldReturnsCollection, where('createdAt', '>=', startDate), where('createdAt', '<=', endDate));

  try {
    const oldReturnsSnapshot = await getDocs(oldReturnsQuery);
    console.log(`🔄 تم العثور على ${oldReturnsSnapshot.docs.length} مرتجع في مجموعة 'sale_returns'`);

    oldReturnsSnapshot.forEach(doc => {
      const data: any = doc.data();
      const createdAt = data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(data.createdAt);
      const dateStr = createdAt.toISOString().split('T')[0];
      const amount = data.totalAmount || 0;
      salesReturnsByDay[dateStr] = (salesReturnsByDay[dateStr] || 0) + amount;
      totalReturns += amount;
      console.log(`🔄 مرتجع قديم: ${amount} في ${dateStr} (مرتجع: ${doc.id.substring(0, 8)})`);
    });
  } catch (error) {
    console.warn('⚠️ خطأ في جلب المرتجعات القديمة:', error);
  }

  // حساب صافي المبيعات (المبيعات النقدية - المرتجعات)
  // المبيعات النقدية ستُحسب من drawerTransactions
  // المرتجعات ستُخصم من drawerTransactions أيضاً
  console.log(`💰 سيتم حساب المبيعات النقدية من drawerTransactions...`);

  // إعداد البيانات اليومية للمبيعات (ستُملأ من drawerTransactions)
  const netSalesByDay: Record<string, number> = {};

  // إعداد متغيرات دفعات الصيانة (ستُحسب من drawerTransactions)
  const maintenanceByDay: Record<string, number> = {};
  let totalMaintenance = 0;

  console.log('🔧 سيتم حساب دفعات الصيانة النقدية من drawerTransactions كـ maintenance_cash_payment...');

  // جلب جميع معاملات الخزينة (دفعات الديون + مدفوعات الصيانة النقدية)
  console.log('🔍 جلب جميع معاملات الخزينة...');
  console.log('📅 فترة البحث:', {
    من: startDate.toISOString().split('T')[0],
    إلى: endDate.toISOString().split('T')[0]
  });
  
  const drawerCollection = getDrawerTransactionsCollection();
  const drawerQuery = branchId
    ? query(drawerCollection, where('branchId', '==', branchId), where('date', '>=', startDate.toISOString().split('T')[0]), where('date', '<=', endDate.toISOString().split('T')[0]))
    : query(drawerCollection, where('date', '>=', startDate.toISOString().split('T')[0]), where('date', '<=', endDate.toISOString().split('T')[0]));
  
  console.log('🔍 تنفيذ استعلام drawerTransactions...');
  const drawerSnapshot = await getDocs(drawerQuery);
  console.log(`📊 تم العثور على ${drawerSnapshot.size} سجل في drawerTransactions`);
  
  const debtPaymentsByDay: Record<string, number> = {};
  const returnsByDay: Record<string, number> = {};
  let totalDebtPayments = 0;
  let totalReturnsFromDrawer = 0;

  drawerSnapshot.forEach(doc => {
    const data: any = doc.data();
    console.log('📄 سجل drawerTransaction:', {
      id: doc.id,
      type: data.type,
      amount: data.amount,
      date: data.date,
      paymentDate: data.paymentDate,
      customerName: data.customerName
    });

    // حساب المبيعات النقدية (تزيد الخزينة)
    if (data.type === 'sale_payment') {
      const paymentDate = data.paymentDate instanceof Timestamp ? data.paymentDate.toDate() : new Date(data.date);
      if (paymentDate >= startDate && paymentDate <= endDate) {
        const dateStr = paymentDate.toISOString().split('T')[0];
        const originalAmount = data.amount || 0;
        const amount = Math.abs(originalAmount); // تأكد من أن المبلغ موجب (يزيد الخزينة)

        salesByDay[dateStr] = (salesByDay[dateStr] || 0) + amount;
        totalSales += amount;
        console.log(`💰 مبيعات نقدية: +${amount} في ${dateStr} (إضافة للخزينة)`);
      } else {
        console.log(`⚠️ مبيعات خارج الفترة: ${paymentDate.toISOString().split('T')[0]} (المبلغ: ${data.amount})`);
      }
    }
    // حساب دفعات الصيانة النقدية (تزيد الخزينة)
    else if (data.type === 'maintenance_cash_payment') {
      const paymentDate = data.paymentDate instanceof Timestamp ? data.paymentDate.toDate() : new Date(data.date);
      if (paymentDate >= startDate && paymentDate <= endDate) {
        const dateStr = paymentDate.toISOString().split('T')[0];
        const originalAmount = data.amount || 0;
        const amount = Math.abs(originalAmount); // تأكد من أن المبلغ موجب (يزيد الخزينة)

        maintenanceByDay[dateStr] = (maintenanceByDay[dateStr] || 0) + amount;
        totalMaintenance += amount;
        console.log(`🔧 دفعة صيانة نقدية: +${amount} في ${dateStr} (إضافة للخزينة)`);
      } else {
        console.log(`⚠️ دفعة صيانة خارج الفترة: ${paymentDate.toISOString().split('T')[0]} (المبلغ: ${data.amount})`);
      }
    }
    // حساب دفعات الديون فقط (تزيد الخزينة)
    // ملاحظة: دفعات الصيانة النقدية تُحسب الآن من paymentTransactions مباشرة
    else if (data.type === 'debt_payment') {
      const paymentDate = data.paymentDate instanceof Timestamp ? data.paymentDate.toDate() : new Date(data.date);
      if (paymentDate >= startDate && paymentDate <= endDate) {
        const dateStr = paymentDate.toISOString().split('T')[0];
        const originalAmount = data.amount || 0;
        const amount = Math.abs(originalAmount); // تأكد من أن المبلغ موجب (يزيد الخزينة)

        debtPaymentsByDay[dateStr] = (debtPaymentsByDay[dateStr] || 0) + amount;
        totalDebtPayments += amount;
        console.log(`💳 دفعة دين: +${amount} في ${dateStr} (إضافة للخزينة)`);
      } else {
        console.log(`⚠️ دفعة دين خارج الفترة: ${paymentDate.toISOString().split('T')[0]} (المبلغ: ${data.amount})`);
      }
    }
    // حساب دفعات المتجر الإلكتروني (تزيد الخزينة)
    else if (data.type === 'store_payment') {
      const paymentDate = data.paymentDate instanceof Timestamp ? data.paymentDate.toDate() : new Date(data.date);
      if (paymentDate >= startDate && paymentDate <= endDate) {
        const dateStr = paymentDate.toISOString().split('T')[0];
        const originalAmount = data.amount || 0;
        const amount = Math.abs(originalAmount); // تأكد من أن المبلغ موجب (يزيد الخزينة)

        salesByDay[dateStr] = (salesByDay[dateStr] || 0) + amount;
        totalSales += amount;
        console.log(`🛒 دفعة متجر إلكتروني: +${amount} في ${dateStr} (إضافة للخزينة)`);
      } else {
        console.log(`⚠️ دفعة متجر خارج الفترة: ${paymentDate.toISOString().split('T')[0]} (المبلغ: ${data.amount})`);
      }
    }
    // حساب مرتجعات المتجر الإلكتروني (تنقص الخزينة)
    else if (data.type === 'store_return') {
      const returnDate = data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(data.date);
      if (returnDate >= startDate && returnDate <= endDate) {
        const dateStr = returnDate.toISOString().split('T')[0];
        const originalAmount = data.amount || 0;
        const amount = Math.abs(originalAmount); // المبلغ سالب في DB، نحوله لموجب للعرض

        // خصم من المبيعات
        salesByDay[dateStr] = (salesByDay[dateStr] || 0) - amount;
        totalSales -= amount;
        console.log(`🔙 إرجاع متجر إلكتروني: -${amount} في ${dateStr} (خصم من الخزينة)`);
      } else {
        console.log(`⚠️ إرجاع متجر خارج الفترة: ${returnDate.toISOString().split('T')[0]} (المبلغ: ${data.amount})`);
      }
    }
    // معالجة maintenance_payment القديمة (للتوافق مع البيانات القديمة)
    else if (data.type === 'maintenance_payment') {
      const paymentDate = data.paymentDate instanceof Timestamp ? data.paymentDate.toDate() : new Date(data.date);
      if (paymentDate >= startDate && paymentDate <= endDate) {
        const dateStr = paymentDate.toISOString().split('T')[0];
        const originalAmount = data.amount || 0;
        const amount = Math.abs(originalAmount);

        maintenanceByDay[dateStr] = (maintenanceByDay[dateStr] || 0) + amount;
        totalMaintenance += amount;
        console.log(`🔧 دفعة صيانة قديمة: +${amount} في ${dateStr} (maintenance_payment)`);
      }
    } else if (data.type === 'return' ||
               data.type === 'maintenance_refund' ||
               data.type === 'sale_return') {
      // معالجة معاملات الإرجاع (تنقص الخزينة)
      const returnDate = data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(data.date);
      if (returnDate >= startDate && returnDate <= endDate) {
        const dateStr = returnDate.toISOString().split('T')[0];
        const amount = Math.abs(data.amount || 0); // المبلغ الفعلي للمرتجع (موجب للعرض)
        returnsByDay[dateStr] = (returnsByDay[dateStr] || 0) + amount;
        totalReturnsFromDrawer += amount;
        console.log(`🔄 إرجاع: -${amount} في ${dateStr} (خصم من الخزينة) - نوع: ${data.type}`);
      } else {
        console.log(`⚠️ إرجاع خارج الفترة: ${returnDate.toISOString().split('T')[0]} (المبلغ: ${data.amount}) - نوع: ${data.type}`);
      }
    } else if (data.type === 'expense') {
      // معالجة المصاريف (تنقص الخزينة)
      const expenseDate = data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(data.date);
      if (expenseDate >= startDate && expenseDate <= endDate) {
        const dateStr = expenseDate.toISOString().split('T')[0];
        const amount = Math.abs(data.amount || 0); // المبلغ الفعلي للمصروف (موجب للعرض)
        returnsByDay[dateStr] = (returnsByDay[dateStr] || 0) + amount; // نضيفها للمرتجعات مؤقتاً
        totalReturnsFromDrawer += amount;
        console.log(`💸 مصروف: -${amount} في ${dateStr} (خصم من الخزينة) - ${data.description || 'مصروف'}`);
      } else {
        console.log(`⚠️ مصروف خارج الفترة: ${expenseDate.toISOString().split('T')[0]} (المبلغ: ${data.amount})`);
      }
    } else {
      console.log(`⚠️ نوع معاملة غير معروف: ${data.type} (سيتم تجاهلها)`);
    }
  });

  console.log(`📊 إجمالي دفعات الديون المضافة للخزينة: +${totalDebtPayments}`);
  console.log(`📊 إجمالي المرتجعات المخصومة من الخزينة: -${totalReturnsFromDrawer}`);
  console.log('🗃️ توزيع مدفوعات الديون حسب اليوم:', debtPaymentsByDay);
  console.log('🗃️ توزيع المرتجعات حسب اليوم:', returnsByDay);

  // تحذير إذا كانت هناك دفعات ديون بقيم سلبية
  const negativeDebtPayments = drawerSnapshot.docs.filter(doc => {
    const data = doc.data();
    return data.type === 'debt_payment' && (data.amount || 0) < 0;
  });

  if (negativeDebtPayments.length > 0) {
    console.warn('🚨 تم اكتشاف دفعات ديون بقيم سلبية:', negativeDebtPayments.map(doc => ({
      id: doc.id,
      amount: doc.data().amount,
      customerName: doc.data().customerName,
      type: doc.data().type
    })));
    console.warn('🔧 سيتم تحويل القيم السلبية إلى موجبة تلقائياً');
  }

  // جلب بيانات المصاريف
  const expensesCollection = getExpensesCollection();
  const expensesQuery = branchId
    ? query(expensesCollection, where('branchId', '==', branchId), where('date', '>=', startDate), where('date', '<=', endDate))
    : query(expensesCollection, where('date', '>=', startDate), where('date', '<=', endDate));
  const expensesSnapshot = await getDocs(expensesQuery);
  const expensesByDay: Record<string, number> = {};
  let totalExpenses = 0;
  expensesSnapshot.forEach(doc => {
    const data: any = doc.data();
    const date = data.date instanceof Timestamp ? data.date.toDate() : new Date(data.date);
    const dateStr = date.toISOString().split('T')[0];
    const amount = data.amount || 0;
    expensesByDay[dateStr] = (expensesByDay[dateStr] || 0) + amount;
    totalExpenses += amount;
  });

  // بناء dailyData مع فصل دفعات الديون والمرتجعات
  const allDates = new Set([
    ...Object.keys(netSalesByDay),
    ...Object.keys(maintenanceByDay),
    ...Object.keys(debtPaymentsByDay),
    ...Object.keys(returnsByDay),
    ...Object.keys(expensesByDay)
  ]);
  const dailyData: Record<string, { sales: number; maintenance: number; debtPayments: number; returns: number; expenses: number; net: number }> = {};
  allDates.forEach(dateStr => {
    const sales = salesByDay[dateStr] || 0; // استخدام المبيعات النقدية
    const maintenance = maintenanceByDay[dateStr] || 0;
    const debtPayments = debtPaymentsByDay[dateStr] || 0;
    const returns = returnsByDay[dateStr] || 0;
    const expenses = expensesByDay[dateStr] || 0;
    dailyData[dateStr] = {
      sales,
      maintenance,
      debtPayments,
      returns,
      expenses,
      net: sales + maintenance + debtPayments - returns - expenses // دفعات الديون تزيد، المرتجعات تنقص، المصاريف تنقص
    };
  });

  const netTotal = totalSales + totalMaintenance + totalDebtPayments - totalReturnsFromDrawer - totalExpenses;

  const result = {
    totalSales: totalSales,
    totalMaintenance,
    totalDebtPayments,
    totalReturnsFromDrawer,
    totalExpenses,
    netTotal,
    dailyData
  };

  // ✅ حفظ في التخزين المؤقت لمدة 3 دقائق (محسّن)
  simpleCache.set(cacheKey, result, 3 * 60 * 1000);

  return result;
};

const reportService = {
  getMaintenanceStats,
  getInventoryStats,
  getSalesStats,
  getMaintenanceDailyIncome,
  getSimpleDrawerStats
};

export default reportService;